import {
  Injectable,
  WritableSignal
} from '@angular/core';
import {
  AbstractControl,
  FormGroup,
  ValidationErrors
} from '@angular/forms';

/** Libellés Aquao des pays disposant d'un format de code postal et d'une liste de communes */
export const FRANCE = 'FRANCE';
const BELGIUM = 'BELGIQUE';
const GERMANY = 'ALLEMAGNE';

const ZIP_FORMATS: Record<string, { length: number }> = {
  [FRANCE]: { length: 5 },
  [BELGIUM]: { length: 4 },
  [GERMANY]: { length: 5 }
};

/**
 * Codes postaux et communes par pays.
 * La France utilise `codes-postaux`, la Belgique et l'Allemagne les données GeoNames (CC BY 4.0, https://www.geonames.org),
 * chargées à la demande. Les autres pays n'ont pas de liste : code postal et commune sont alors en saisie libre.
 */
@Injectable({ providedIn: 'root' })
export class PostalCodeService {
  private cache = new Map<string, Promise<Record<string, string[]>>>();

  /**
   * Indique si le pays dispose d'une liste de communes (la commune est alors à choisir dans la liste)
   * @param country Libellé Aquao du pays, vide pour la France
   * @returns Vrai si une liste de communes existe
   */
  hasCityList(country: string): boolean {
    return (country || FRANCE) in ZIP_FORMATS;
  }

  /**
   * Nettoie un code postal selon le format du pays
   * @param country Libellé Aquao du pays, vide pour la France
   * @param zip Valeur saisie
   * @returns Chiffres seuls tronqués à la longueur du pays, ou la valeur saisie sans espaces superflus
   */
  normalizeZip(country: string, zip: string): string {
    const format = ZIP_FORMATS[country || FRANCE];
    return format ? zip.replace(/[^0-9]/g, '').slice(0, format.length) : zip.trim();
  }

  /**
   * Validateur de code postal, qui lit le pays dans le champ `country` du même formulaire
   * @param control Champ du code postal
   * @returns Une erreur `zipFormat` si le code postal n'a pas le format du pays
   */
  zipValidator = (control: AbstractControl<string>): ValidationErrors | null => {
    const format = ZIP_FORMATS[control.parent?.get('country')?.value || FRANCE];
    if (!format || !control.value) {
      return null;
    }
    return new RegExp(`^\\d{${format.length}}$`).test(control.value) ? null : { zipFormat: true };
  };

  /**
   * Communes correspondant à un code postal
   * @param country Libellé Aquao du pays, vide pour la France
   * @param zip Code postal déjà normalisé
   * @returns Les communes, ou null si le pays n'a pas de liste ou si le code postal est inconnu
   */
  async cities(country: string, zip: string): Promise<string[] | null> {
    const key = country || FRANCE;
    if (!zip || !(key in ZIP_FORMATS)) {
      return null;
    }
    let cities: string[];
    if (key === FRANCE) {
      const { default: codePostaux } = await import('codes-postaux');
      cities = (codePostaux.find(zip) as { nomCommune: string }[]).map(c => c.nomCommune);
    } else {
      cities = (await this.load(key))[zip] ?? [];
    }
    return cities.length ? cities : null;
  }

  /**
   * Relie les champs `zip`, `country` et `city` d'un formulaire : nettoie le code postal selon le pays
   * et alimente la liste de communes proposées.
   * @param form Formulaire contenant les champs `zip`, `city` et `country`
   * @param cities Signal recevant les communes proposées
   * @param clearCity Vide la commune à chaque changement de code postal
   * @param isIdle Retourne vrai quand les changements doivent être ignorés (formulaire désactivé)
   */
  bind(form: FormGroup, cities: WritableSignal<string[] | null>, clearCity = false, isIdle = (): boolean => false): void {
    const zip = form.get('zip')!;
    const city = form.get('city')!;
    const country = form.get('country')!;
    let latest = 0;
    const refresh = async (): Promise<void> => {
      if (isIdle()) {
        return;
      }
      const normalized = this.normalizeZip(country.value, zip.value ?? '');
      if (normalized !== zip.value) {
        zip.setValue(normalized, { emitEvent: false });
      }
      const request = ++latest;
      const found = await this.cities(country.value, normalized);
      // Une réponse obsolète (saisie ou pays modifié pendant le chargement) ne doit pas écraser la dernière
      if (request === latest) {
        cities.set(found);
      }
    };
    zip.valueChanges.subscribe(() => {
      if (clearCity && !isIdle()) {
        city.setValue('');
      }
      void refresh();
    });
    // disable() et enable() réémettent la valeur du pays : seul un vrai changement de pays doit vider la commune
    let currentCountry = country.value;
    country.valueChanges.subscribe(value => {
      if (value === currentCountry) {
        return;
      }
      currentCountry = value;
      city.setValue('');
      latest++;
      cities.set(null);
      zip.updateValueAndValidity();
      void refresh();
    });
  }

  private load(country: string): Promise<Record<string, string[]>> {
    let data = this.cache.get(country);
    if (!data) {
      data = country === BELGIUM
        ? import('../data/postal-codes/be.json').then(m => m.default as Record<string, string[]>)
        : import('../data/postal-codes/de.json').then(m => m.default as Record<string, string[]>);
      this.cache.set(country, data);
    }
    return data;
  }
}
